import { useMemo, useState } from 'react'

// Multi-select with controlled-vocabulary options. Used for the carried-by
// relation: sportsbooks under a sport, sports (+ optional sub-division)
// under a sportsbook. Differs from StringList in that entries map to FK
// ids, not freeform text — so the editor can't introduce a name that
// isn't already in the catalog.
//
// UX mirrors StateGrid: every option renders as a toggle cell, click
// toggles selection. Filter input + ALL / CLEAR bulk actions handle
// longer lists without making the user repeat-pick from a dropdown.
//
// Each option may carry a `divisionId`. A sport with sub-divisions
// (e.g. World of Outlaws → Sprint Car / Late Model / Super Dirt Car
// Series) is flattened by the caller into one option per granularity:
//   { id: <sport.id>, label: 'World of Outlaws' }                    — whole
//   { id: <sport.id>, label: 'World of Outlaws — Sprint Car', divisionId: <X> }
//   ...
// The composite (id, divisionId) is the row in sport_sportsbooks.

export interface RelationOption {
  id: number
  divisionId?: number | null
  label: string
}

export interface RelationSelection {
  id: number
  divisionId?: number | null
}

interface Props {
  label: string
  options: RelationOption[]
  selected: RelationSelection[]
  onChange: (next: RelationSelection[]) => void
  readOnly?: boolean
}

function keyOf(s: { id: number, divisionId?: number | null }): string {
  return `${s.id}::${s.divisionId ?? ''}`
}

export default function RelationPicker({
  label,
  options,
  selected,
  onChange,
  readOnly = false,
}: Props) {
  const [filter, setFilter] = useState('')
  const selectedKeys = useMemo(() => new Set(selected.map(keyOf)), [selected])

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return options
    return options.filter(o => o.label.toLowerCase().includes(q))
  }, [filter, options])

  function toggle(opt: RelationOption) {
    if (readOnly) return
    const k = keyOf(opt)
    if (selectedKeys.has(k)) {
      onChange(selected.filter(s => keyOf(s) !== k))
      return
    }
    onChange([...selected, { id: opt.id, divisionId: opt.divisionId ?? null }]
      .sort((a, b) => keyOf(a).localeCompare(keyOf(b))))
  }

  function selectAllVisible() {
    if (readOnly) return
    const merged = new Map<string, RelationSelection>()
    for (const s of selected) merged.set(keyOf(s), s)
    for (const o of visible) {
      merged.set(keyOf(o), { id: o.id, divisionId: o.divisionId ?? null })
    }
    onChange([...merged.values()].sort((a, b) => keyOf(a).localeCompare(keyOf(b))))
  }

  function clear() {
    if (readOnly) return
    onChange([])
  }

  return (
    <div className={`ad-grid-wrap${readOnly ? ' is-read-only' : ''}`}>
      <div className="ad-grid-head">
        <span className="b-mono">{label}</span>
        <span className="b-mono-dim b-tiny">{selected.length} / {options.length} SELECTED</span>
        {!readOnly && (
          <div className="ad-grid-bulk">
            <button type="button" onClick={selectAllVisible} className="b-btn-ghost b-btn-tiny">
              {filter.trim() ? 'ADD ALL VISIBLE' : 'ALL'}
            </button>
            <button type="button" onClick={clear} className="b-btn-ghost b-btn-tiny">CLEAR</button>
          </div>
        )}
      </div>
      <input
        type="text"
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="filter…"
        className="ad-relation-filter"
        spellCheck={false}
      />
      {visible.length === 0
        ? <p className="b-mono-dim ad-list-empty">no matches</p>
        : (
            <div className="ad-relation-grid">
              {visible.map((opt) => {
                const isOn = selectedKeys.has(keyOf(opt))
                return (
                  <button
                    key={keyOf(opt)}
                    type="button"
                    onClick={() => toggle(opt)}
                    disabled={readOnly}
                    className={`ad-relation-cell${isOn ? ' is-on' : ''}`}
                    aria-pressed={isOn}
                  >
                    <span className="ad-relation-mark" aria-hidden="true">{isOn ? '✓' : ' '}</span>
                    <span className="ad-relation-label">{opt.label}</span>
                  </button>
                )
              })}
            </div>
          )}
    </div>
  )
}
